import React, { useMemo } from 'react';
import SnakeGame from '../components/SnakeGame';
import { GAME_MODES, GAME_SKINS } from '../data/siteContent';
import { useSiteContext } from '../context/SiteContext';

const PlayPage: React.FC = () => {
  const { isSignedIn, preferences, profile, scores, recordScore, updatePreferences } =
    useSiteContext();

  const selectedMode =
    GAME_MODES.find((mode) => mode.id === preferences.activeModeId) ?? GAME_MODES[0];
  const selectedSkin =
    GAME_SKINS.find((skin) => skin.id === preferences.activeSkinId) ?? GAME_SKINS[0];

  const personalBest = useMemo(() => {
    const nickname = profile?.nickname;

    return scores
      .filter((entry) => !nickname || entry.nickname === nickname)
      .reduce((bestScore, entry) => Math.max(bestScore, entry.score), 0);
  }, [profile, scores]);

  const modeBest = useMemo(() => {
    return scores
      .filter((entry) => entry.modeId === selectedMode.id)
      .reduce((bestScore, entry) => Math.max(bestScore, entry.score), 0);
  }, [scores, selectedMode.id]);

  return (
    <div className="page page--play">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Enter the arena</p>
          <h1>Choose your mode. Light up the grid. Chase the crown.</h1>
          <p>
            Lock in a ruleset, style your board, and launch straight into a score-hunting run
            built for quick reflexes.
          </p>
        </div>
        <div className="page-heading__meta">
          <span className="site-chip">Your best: {personalBest}</span>
          <span className="site-chip">{selectedMode.name} record: {modeBest}</span>
        </div>
      </section>

      <section className="play-layout">
        <aside className="play-sidebar">
          <article className="control-panel">
            <div className="section-heading section-heading--compact">
              <p className="eyebrow">Game mode</p>
              <h2>Choose your challenge</h2>
            </div>

            <div className="choice-grid">
              {GAME_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  className={`choice-card ${
                    selectedMode.id === mode.id ? 'choice-card--active' : ''
                  }`}
                  onClick={() => updatePreferences({ activeModeId: mode.id })}
                >
                  <span className="site-chip">{mode.badge}</span>
                  <strong>{mode.name}</strong>
                  <p>{mode.description}</p>
                </button>
              ))}
            </div>
          </article>

          <article className="control-panel">
            <div className="section-heading section-heading--compact">
              <p className="eyebrow">Visual skin</p>
              <h2>Style your arena</h2>
            </div>

            <div className="choice-grid">
              {GAME_SKINS.map((skin) => (
                <button
                  key={skin.id}
                  type="button"
                  className={`choice-card ${
                    selectedSkin.id === skin.id ? 'choice-card--active' : ''
                  }`}
                  onClick={() => updatePreferences({ activeSkinId: skin.id })}
                >
                  <span className="choice-card__swatch" style={{ background: skin.palette.accent }} />
                  <strong>{skin.name}</strong>
                  <p>{skin.description}</p>
                </button>
              ))}
            </div>
          </article>

          <article className="control-panel control-panel--muted">
            <div className="section-heading section-heading--compact">
              <p className="eyebrow">Current challenger</p>
              <h2>{isSignedIn && profile ? profile.nickname : 'Guest session'}</h2>
            </div>
            <p>
              Sign in to keep your alias on the board and carry your favorite setup from run to
              run.
            </p>
          </article>
        </aside>

        <div className="play-stage">
          <SnakeGame
            mode={selectedMode}
            skin={selectedSkin}
            playerName={profile?.nickname || 'Guest'}
            highScore={modeBest}
            onScoreRecorded={(score, durationMs) =>
              recordScore({
                modeId: selectedMode.id,
                skinId: selectedSkin.id,
                score,
                durationMs,
                nickname: profile?.nickname || 'Guest'
              })
            }
          />
        </div>
      </section>
    </div>
  );
};

export default PlayPage;
